import React, { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { OverlayPanel } from 'primereact/overlaypanel';

import { ColumnConfig } from './COLUMN_CONFIG';

interface ColumnVisibilityToggleProps {
  columns: ColumnConfig[];
  visibleColumns: ColumnConfig[];
  onApply: (columns: ColumnConfig[]) => void;
  hasHorizontalScroll: boolean;
}

const ColumnVisibilityToggle: React.FC<ColumnVisibilityToggleProps> = ({
  columns,
  visibleColumns,
  onApply,
  hasHorizontalScroll
}) => {
  const [selectedColumns, setSelectedColumns] =
    useState<ColumnConfig[]>(visibleColumns);
  const [selectAll, setSelectAll] = useState<boolean>(
    visibleColumns?.length === columns?.length
  );
  const [isOverlayVisible, setIsOverlayVisible] = useState<boolean>(false);
  const [isApplyClicked, setIsApplyClicked] = useState<boolean>(false);
  const op = useRef<OverlayPanel>(null);

  const handleApply = (
    e: React.SyntheticEvent<Element, Event> | null | undefined
  ) => {
    onApply(selectedColumns);
    setIsApplyClicked(true);
    op.current?.hide();
  };

  const handleCheckboxChange = (column: ColumnConfig) => {
    const isSelected = selectedColumns.some(
      (col) => col.field === column.field
    );
    if (isSelected) {
      setSelectedColumns(
        selectedColumns.filter((col) => col.field !== column.field)
      );
    } else {
      setSelectedColumns([...selectedColumns, column]);
    }
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(columns);
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    if (!isOverlayVisible && !isApplyClicked) {
      setSelectedColumns(visibleColumns);
      setSelectAll(visibleColumns?.length === columns?.length);
    }
  }, [isOverlayVisible, isApplyClicked, visibleColumns, columns]);

  useEffect(() => {
    setSelectAll(selectedColumns?.length === columns?.length);
  }, [selectedColumns]);

  return (
    <div style={{ position: 'relative' }}>
      <Button
        key={`absencePersons-exportTemplate-btn-${hasHorizontalScroll}`}
        icon="pi pi-eye"
        tooltip="Выбор столбцов таблицы для отображения"
        tooltipOptions={{ position: hasHorizontalScroll ? 'left' : 'top' }}
        onClick={(e) => {
          setIsApplyClicked(false);
          op.current?.toggle(e);
        }}
        className="p-button-rounded"
        severity={
          visibleColumns.length < columns.length ? 'warning' : 'secondary'
        }
        id="tasks-visibility-toggle"
      />
      <OverlayPanel
        className="treeDropdownCheckbox"
        ref={op}
        showCloseIcon
        dismissable={true}
        onShow={() => setIsOverlayVisible(true)}
        onHide={() => setIsOverlayVisible(false)}
      >
        <div className="flex flex-column gap-3">
          {/* Select All checkbox */}
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="selectAll"
              checked={selectAll}
              onChange={handleSelectAllChange}
            />
            <label htmlFor="selectAll" className="ml-2 font-normal">
              Выбрать все столбцы
            </label>
          </div>
          <Divider className="m-0" />

          {columns.map((column) => (
            <div key={column.field} className="flex align-items-center gap-2">
              <Checkbox
                inputId={column.field}
                checked={selectedColumns.some(
                  (col) => col.field === column.field
                )}
                onChange={() => handleCheckboxChange(column)}
              />
              <label htmlFor={column.field} className="ml-2 font-normal">
                {column.header}
              </label>
            </div>
          ))}
          <Button
            label="Применить"
            onClick={(e) => handleApply(e)}
            className="p-button-sm align-self-end"
            icon="pi pi-check"
          />
        </div>
      </OverlayPanel>
    </div>
  );
};

export default ColumnVisibilityToggle;
